import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable, forwardRef, inject } from '@angular/core';
import { LatestRoleNPermissions, Roles } from '@equine-ui-nx/ui';
import { BehaviorSubject, Observable } from 'rxjs';
import { PublicUrl } from '../interface/auth.interface';
import { UPDATE_USER } from '@equine-ui-nx/injection-tokens';

export interface ISessionData {
  uuid: number;
  application: string;
  device: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  isLoggedIn: boolean;
  userRole = undefined;

  userPermission = [];
  private loggedInUserSubject$ = new BehaviorSubject<Record<
    string,
    unknown
  > | null>(null);
  private userPermission$ = new BehaviorSubject<any | null>(null);
  private selectedSeason$ = new BehaviorSubject<any | null>(null);
  private profilePic$ = new BehaviorSubject<string>('');
  private seasonSelectionConfirmation$ = new BehaviorSubject<boolean | null>(null);
  isSsoLogin = false;

  private http = inject(HttpClient);

  constructor(
    @Inject('environment') private environment: any,
    @Inject(forwardRef(() => UPDATE_USER))
    private updateUserToken: BehaviorSubject<void>
  ) {
    const token = localStorage.getItem('accessToken');
    const userDataLS = localStorage.getItem('data');
    const userPermission = localStorage.getItem('permissions');

    if (token && userDataLS) {
      this.loggedInUserSubject$.next(JSON.parse(userDataLS));
      this.isLoggedIn = true;
    } else {
      this.isLoggedIn = false;
    }
    if (userPermission) {
      this.userPermission$.next(JSON.parse(userPermission));
    }
    this.listenToUserUpdate();
  }

  public listenToLoggedinUser() {
    return this.loggedInUserSubject$.asObservable();
  }

  public listenToSeasonSelectionConfirmation() {
    return this.seasonSelectionConfirmation$.asObservable();
  }

  public setSeasonSelectionConfirmation(flag: boolean) {
    return this.seasonSelectionConfirmation$.next(flag);
  }

  public emitLoggedinUser(value: any) {
    return this.loggedInUserSubject$?.next(value);
  }

  public getLoggedInUser() {
    return this.loggedInUserSubject$?.value;
  }
  public listenToSelectedSeason() {
    return this.selectedSeason$.asObservable();
  }

  public emitSelectedSeason(value: any) {
    return this.selectedSeason$?.next(value);
  }

  public getSelectedSeason() {
    return this.selectedSeason$?.value;
  }

  openOBOwindow(data: any) {
    let obo: any = {};
    for (let i = 0; i < localStorage.length; i++) {
      const key: any = localStorage.key(i);
      obo[key] = localStorage.getItem(key);
    }
    this.setDataToSessionStg("obo", obo)
    const redirectUrl = `${window.location.origin}/account/callback?accessToken=${encodeURIComponent(data?.accessToken)}&refreshToken=${encodeURIComponent(data?.refreshToken)}&userDataString=${encodeURIComponent(data?.userData)}&redirectUrl=dashboard`;
    localStorage.clear();
    window.open(redirectUrl, '_self');
  }

  setDataToSessionStg(key: string, data: any){
    sessionStorage.setItem(key, JSON.stringify(data))
  }
  getDataFromSessionStg(key: string){
    const data = sessionStorage.getItem(key);
    if(!data) return null
    return JSON.parse(data) 
  }
  closeOBOWindow(){
    const data = this.getDataFromSessionStg("obo")
    const redirectUrl = `${window.location.origin}/account/callback?accessToken=${encodeURIComponent(data?.accessToken)}&refreshToken=${encodeURIComponent(data?.refreshToken)}&userDataString=${encodeURIComponent(data?.data)}&redirectUrl=dashboard&selectedSeason=${encodeURIComponent(data?.selectedSeason)}`;
    localStorage.clear();
    sessionStorage.clear();
    window.open(redirectUrl, '_self');
  }
  isAuthenticated() {
    return this.isLoggedIn;
  }

  getUserRole(): string {
    // Need to write logic for to get roles from token for users.
    // we will ask option to select role if he has multiple roles
    const user = this.getUserData();
    return user?.roles[0]?.name;
  }

  getUserRoles(): string[] {
    const user = this.getUserData();
    const userRoles: string[] = [];
    for (let i = 0; i < user?.roles?.length; i++) {
      if (user?.roles[i]?.name) {
        userRoles.push(user?.roles[i]?.name);
      }
    }
    return userRoles;
  }

  isAdminUser(): boolean {
    if([Roles.SUPER_ADMIN.toString(), Roles.ACCOUNT_ADMIN.toString(), Roles.ADMIN.toString(), Roles.ZONE_ADMIN.toString()].includes(this.getUserRole())) {
      return true;
    }
    return false;
  }

  getUserId(): string {
    const user = this.getUserData();
    return user?._id;
  }

  getUserEmail(): string {
    const user = this.getUserData();
    return user?.email;
  }

  logout() {
    this.isLoggedIn = false;
    sessionStorage.removeItem("requestedUrl")
    // Your logout logic here
    setTimeout(() => {
      this.emitLoggedinUser(null);
    }, 500);
  }

  clearSeasonData() {
    localStorage.removeItem("selectedSeason");
  }

  cleanLocalStorage() {
    const k = localStorage.getItem("doNotShowTipAgain") // keep this key in place even user loggedOut #ArenaEQ 
    localStorage.clear();
    if(k){
      localStorage.setItem("doNotShowTipAgain", k);
    }
    this.setSessionData();
  }

  setSessionData() {
    const date = Date.now();
    const sessionData: ISessionData = {
      uuid: date,
      application: 'super-admin',
      device: 'chrome',
    };
    localStorage.setItem('sessionData', JSON.stringify(sessionData));
  }

  verifyToken(payload: any) {
    return this.http.post(
      `${this.environment.apiUrl}/login/verifyToken`,
      payload
    );
  }

  getUserPermissionByRole(roleName: any) {
    return this.http.post(
      `${this.environment.apiUrl}/login/getUserPermissions`,
      { roleName }
    );
  }

  async decodeAccessToken() {
    let accessToken = await this.getAccessToken();
    let decodedToken = accessToken
      ? JSON.parse(atob(accessToken.split('.')[1]))
      : '';
    if (decodedToken?.role) this.userRole = decodedToken?.role;
    if (decodedToken?.permissions)
      this.userPermission = decodedToken?.permissions;
  }

  getAccessToken() {
    return localStorage.getItem('accessToken');
  }

  setIsUserLogin(isLogin: boolean) {
    this.isLoggedIn = isLogin;
  }

  getURL() {
    let url = 'dashboard';
    const role = this.getUserRole();
    if (
      role == Roles.SUPER_ADMIN ||
      role == Roles.ACCOUNT_ADMIN ||
      role == Roles.ZONE_ADMIN ||
      role == Roles.ADMIN
    ) {
      url = 'dashboard';
    }
    return url;
  }

  setAccessToken(token: string) {
    return localStorage.setItem('accessToken', token);
  }

  removeAccessToken() {
    localStorage.removeItem('accessToken');
  }

  setRefreshToken(refreshToken: string) {
    return localStorage.setItem('refreshToken', refreshToken);
  }

  setUserPermissions(permissions: string) {
    return localStorage.setItem('permissions', permissions);
  }

  setToken(token: string) {
    localStorage.setItem('token', token);
  }
  setRequestedUrl(url: string) {
    url = url.replace(location.origin, '')
    sessionStorage.setItem('requestedUrl', url);
  }
  getRequestedUrl(): string | null | void{
    const requestedUrl = sessionStorage.getItem('requestedUrl');
    return requestedUrl;
  }
  saveUserDataInLocalStorage(userDataAsString: string) {
    localStorage.setItem('data', userDataAsString);
  }

  getSessionData(): string | null {
    return localStorage.getItem('sessionData');
  }

  getToken(): string | null {
    return localStorage.getItem('token');
  }

  getNewAccessToken(refreshToken: any) {
    return this.http.post(
      `${this.environment.apiUrl}/login/generateNewAccessToken`,
      refreshToken
    );
  }

  getUserData(): any {
    const data: any = localStorage.getItem('data');
    return data ? JSON.parse(data) : null;
  }

  getTermsSignData(): any {
    const data: any = localStorage.getItem('termsSignLater');
    return data == 'true' ? data : false;
  }

  setTermsSignLaterData(): any {
    localStorage.setItem('termsSignLater', 'true');
  }

  public setTermSignatured() {
    let data: any = localStorage.getItem('data');
    data = JSON.parse(data);
    data['acceptAllTerms'] = true;
    localStorage.setItem('data', JSON.stringify(data));
  }

  getUserPermissions(): any {
    const permissions: any = localStorage.getItem('permissions');
    return permissions ? JSON.parse(permissions) : null;
  }

  getIEAAccountId(): string {
    const user = this.getUserData();
    return user?.IEAAccountId;
  }

  tokenUpdate(data: any) {
    return this.http.patch(`${this.environment.apiUrl}/login/logout`, data);
  }

  removeRefreshToken() {
    localStorage.removeItem('refreshToken');
  }

  removeUserData() {
    localStorage.removeItem('data');
  }

  removeUserPermission() {
    localStorage.removeItem('permissions');
  }

  removeCacheUrl() {
    localStorage.removeItem('url');
  }

  updateData(updatedData: any) {
    const userData = this.getUserData();
    this.saveUserDataInLocalStorage(
      JSON.stringify({ ...userData, ...updatedData })
    );
  }

  async getAuthStatus() {
    let accessToken = await this.getAccessToken();
    if (accessToken) {
      return this.isLoggedIn;
    } else {
      this.isLoggedIn = false;
      return false;
    }
  }

  getSecuredUrl(key: string): Observable<PublicUrl> {
    const url = `${this.environment.apiUrl}/file/url?key=${key}`;
    return this.http.get<PublicUrl>(url);
  }

  setProfilePicUrl(url: string): void {
    this.profilePic$.next(url);
  }

  getProfilePicUrl() {
    return this.profilePic$.asObservable();
  }

  listenToUserUpdate() {
    this.updateUserToken.subscribe({
      next: () => {
        this.fetchUser();
      },
    });
  }

  fetchUser() {
    this.getLatestRoleNPermission().subscribe({
      next: (response: LatestRoleNPermissions) => {
        this.setUserPermissions(JSON.stringify(response.permissions));
        this.saveUserDataInLocalStorage(JSON.stringify(response?.user));
        this.emitLoggedinUser(response?.user);
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  getLatestRoleNPermission(): Observable<LatestRoleNPermissions> {
    const token = this.getAccessToken();
    const headers: any = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });
    const userId = this.getUserId();
    // const seasonId = this. 
    const url = `${this.environment.apiUrl}/login/getUserLatestRoleNPermissions`;
    return this.http.post<LatestRoleNPermissions>(url, { userId }, { headers });
  }

  removeTokens() {
    localStorage.removeItem('token');
  }

  isAdmin(): boolean {
    if ([Roles.SUPER_ADMIN.toString(), Roles.ACCOUNT_ADMIN.toString(), Roles.ADMIN.toString(), Roles.ACCOUNT_ADMIN.toString()].includes(this.getUserRole())) {
      return true;
    }
    return false;
  }

  getUserDataForSelectedSeason(seasonId: string, userId: string): Observable<any> {
    const url = `${this.environment.apiUrl}/login/user-data?seasonId=${seasonId}&userId=${userId}`;
    return this.http.get<any>(url);
  }
}
